import { useCallback, useEffect, useState } from "react";
import type { ChatClient } from "../client";
import type { Session } from "../types";

export interface UseSessionsResult {
  sessions: Session[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  rename: (id: string, title: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** True while the server may hold more sessions than currently loaded. */
  hasMore: boolean;
  /** Append the next page to the list (sidebar "Show more"). */
  loadMore: () => Promise<void>;
}

// Sidebar page size. Reload fetches the first page; loadMore appends the next.
// A full page returned ⇒ assume more exist (one extra empty fetch at the exact
// boundary is acceptable — no total-count endpoint).
export const SESSIONS_PAGE_SIZE = 30;

export function useSessions(client: ChatClient): UseSessionsResult {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await client.listSessions({ limit: SESSIONS_PAGE_SIZE, offset: 0 });
      setSessions(list);
      setHasMore(list.length >= SESSIONS_PAGE_SIZE);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client]);

  const loadMore = useCallback(async () => {
    try {
      const next = await client.listSessions({
        limit: SESSIONS_PAGE_SIZE,
        offset: sessions.length,
      });
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        return [...prev, ...next.filter((s) => !seen.has(s.id))];
      });
      setHasMore(next.length >= SESSIONS_PAGE_SIZE);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [client, sessions.length]);

  useEffect(() => { reload(); }, [reload]);

  const rename = useCallback(
    async (id: string, title: string) => {
      await client.renameSession(id, title);
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
    },
    [client]
  );

  const remove = useCallback(
    async (id: string) => {
      await client.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    },
    [client]
  );

  return { sessions, loading, error, reload, rename, remove, hasMore, loadMore };
}
